'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Button from '@/ui/components/shared/Button';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { paths } from '@/constants/paths';
import { ArrowRightStartOnRectangleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useLogout } from '@/ui/hooks/auth/useLogout';
import { useLogin } from '@/ui/hooks/auth/useLogin';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useTranslations } from 'next-intl';
import useUser from '@/ui/providers/userProvider';
import clsx from 'clsx';
import UserIcon from '../../../../../../../public/images/header/user-icon.svg';

export default function HeaderUserButton({
  setShowClosingOverlay,
  setCloseOverlayFunction,
}: Readonly<{
  setShowClosingOverlay: (show: boolean) => void;
  setCloseOverlayFunction: (close: () => void) => void;
}>) {
  const [open, setOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const { localizedLink } = useLocaleLink();
  const { logout } = useLogout();
  const { login } = useLogin();
  const { baseUrl } = useLocalization();
  const { session, customer } = useUser();

  const t = useTranslations('HeaderUserButton');

  useEffect(() => {
    if (session?.active && customer) {
      setUserName(customer.firstName || '');
      setUserEmail(customer.email || '');
    }
  }, [session, customer]);

  useEffect(() => {
    if (open) {
      setShowClosingOverlay(true);
      setCloseOverlayFunction(() => () => setOpen(false));
    } else {
      setShowClosingOverlay(false);
    }
  }, [open]);

  return (
    <div className="h-full">
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        className="z-20 flex h-full items-center px-2 duration-200 ease-in hover:bg-neutral-5"
        aria-label={t('userMenu')}
        aria-expanded={open}
        data-tile-element-id="nav-user-menu"
      >
        {open ? (
          <XMarkIcon className="h-6 w-auto" aria-hidden="true" />
        ) : (
          <Image src={UserIcon} alt="" width={24} height={24} aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          className={clsx(
            'absolute right-0 top-[105%] w-full rounded-md bg-white p-6 shadow-nuki-blured',
            {
              'max-w-md xs:max-w-xs': !session?.active,
              'max-w-md': session?.active,
            },
          )}
        >
          {session?.active ? (
            <>
              <div className="border-b border-neutral-5 pb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xl font-bold">
                      {`${t('Hello')} ${userName || ''}`}
                      <span className="text-primary-100">!</span>
                    </p>

                    <p className="textlg text-neutral-50">{userEmail || ''}</p>
                  </div>
                  <Button
                    onClick={() => logout(baseUrl)}
                    variant="destructive"
                    className="flex h-fit items-center gap-1 py-2 text-sm"
                    data-tile-element-id="nav-user-logout"
                  >
                    <ArrowRightStartOnRectangleIcon className="h-6 w-auto" />
                    {t('Logout')}
                  </Button>
                </div>
              </div>
              <div className="space-y-4 pt-6">
                <Link
                  className="block w-fit font-bold hover:underline"
                  onClick={() => setOpen(false)}
                  href={localizedLink(paths.myAccount)}
                  data-tile-element-id="nav-user-my-account"
                >
                  {t('Account Dashboard')}
                </Link>
                <Link
                  className="block w-fit font-bold hover:underline"
                  onClick={() => setOpen(false)}
                  href={localizedLink(paths.club)}
                  data-tile-element-id="nav-user-nuki-club"
                >
                  {t('Nuki Club')}
                </Link>
              </div>
            </>
          ) : (
            <>
              <Button
                onClick={() => login(`${baseUrl}${paths.myAccount}`)}
                variant="primary"
                className="block w-full"
                data-tile-element-id="nav-user-login"
              >
                {t('Login')}
              </Button>
              <p className="mt-4 text-center">
                {t.rich('orRegister', {
                  // eslint-disable-next-line react/no-unstable-nested-components
                  registerLink: (chunks) => (
                    <Link
                      href={localizedLink(t('registerUrl'))}
                      className="text-blue-500 font-bold underline"
                      data-tile-element-id="nav-user-register"
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </p>
              <hr className="my-6 border-neutral-5" />
              <Button
                href={paths.nukiWebLogin}
                openInNewTab
                variant="secondary"
                className="block w-full"
                data-tile-element-id="nav-user-nuki-web-login"
              >
                {t('Nuki Web Login')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
